import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * Matches `product-media-gallery-content-styles`: grid is visible from 750px, slideshow below.
 */
const GRID_DESKTOP_MEDIA = '(min-width: 750px)';

/**
 * Tracks last applied variant fetch generation per section/dialog. Must not live on `MediaGallery` because
 * stale lower-generation events must not overwrite good UI after a superseded fetch.
 *
 * @type {WeakMap<Element, { lastGen: number, scopeId: string | undefined }>}
 */
const variantApplyStateByAnchor = new WeakMap();

/**
 * @param {Element} anchor
 */
function getVariantApplyState(anchor) {
  let state = variantApplyStateByAnchor.get(anchor);
  if (!state) {
    state = { lastGen: 0, scopeId: undefined };
    variantApplyStateByAnchor.set(anchor, state);
  }
  return state;
}

/**
 * Ordered media IDs from slideshow slides, or grid items if there are no slides.
 * @param {Element} root
 * @returns {string[]}
 */
function collectGalleryMediaIds(root) {
  const slideMedia = root.querySelectorAll(':scope slideshow-slide .product-media[data-media-id]');
  const nodes = slideMedia.length
    ? slideMedia
    : root.querySelectorAll(':scope .media-gallery__grid .product-media[data-media-id]');

  /** @type {string[]} */
  const ids = [];

  for (const node of nodes) {
    const id = node.getAttribute('data-media-id');
    if (id) ids.push(id);
  }

  return ids;
}

/**
 * @param {string[]} left
 * @param {string[]} right
 * @returns {boolean}
 */
function mediaIdsEqualOrdered(left, right) {
  if (left.length !== right.length) return false;

  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) return false;
  }

  return true;
}

/**
 * @param {string[]} left
 * @param {string[]} right
 * @returns {boolean}
 */
function mediaIdsEqualSet(left, right) {
  if (left.length !== right.length) return false;

  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();

  return mediaIdsEqualOrdered(sortedLeft, sortedRight);
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#onSlideshowSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /**
   * True when variant media sync moved the carousel off the featured slide (index 0).
   * A buyer swipe, thumbnail, or zoom choice clears it so a later same-order variant
   * change does not pull them back to the first image.
   * @type {boolean}
   */
  #variantLeftFeaturedSlide = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Jump to an already-rendered media item (carousel / mobile grid-as-carousel).
   * Does not reorder slides, grid, or zoom — those three must stay in the same order
   * or zoom indices desync. Sync the zoom strip to this index (no closed-dialog scroll).
   *
   * @param {string} mediaId
   * @returns {boolean} Whether a matching slide was found
   */
  #showMediaById(mediaId) {
    if (!mediaId) return false;

    const slideshow = this.slideshow;
    const slides = slideshow?.refs?.slides;
    if (!slideshow || !slides?.length) return false;

    const index = slides.findIndex((slide) => {
      return Boolean(slide.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`));
    });

    if (index < 0) return false;

    // Featured media is already slide 0. Selecting it would pull a buyer who swiped
    // to another photo back to the front. Only do that when a variant select pinned
    // the carousel on a different slide (the level-1 round trip).
    if (index === 0 && slideshow.current !== 0 && !this.#variantLeftFeaturedSlide) {
      return true;
    }

    void slideshow.select(index, undefined, { animate: false });
    this.refs.zoomDialogComponent?.alignToGalleryIndex(index);
    this.#variantLeftFeaturedSlide = index !== 0;
    return true;
  }

  /**
   * @param {HTMLElement} picker
   * @param {string} optionValueId
   * @returns {HTMLElement | null}
   */
  #optionElementFor(picker, optionValueId) {
    if (!optionValueId) return null;
    const option = picker.querySelector(`[data-option-value-id="${CSS.escape(optionValueId)}"]`);
    return option instanceof HTMLElement ? option : null;
  }

  /**
   * Optimistic slide change on a committed selection, only when:
   * - no other variant HTML fetch is already in flight (size radios still have the previous
   *   colour's featured-media ids until that morph lands)
   * - not a combined-listing product switch
   * - the featured media is already in this gallery
   *
   * @param {Event} event
   */
  #handleVariantSelected = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.tagName !== 'VARIANT-PICKER') return;
    if (event.target.closest('quick-add-dialog, product-card')) return;
    if (event.target.hasAttribute('data-variant-html-pending')) return;

    const selectedEvent = /** @type {import('@theme/events').VariantSelectedEvent} */ (event);
    const optionValueId = selectedEvent.detail?.resource?.id;
    const option = this.#optionElementFor(event.target, optionValueId);
    if (!option) return;

    const connected = option.dataset.connectedProductUrl;
    const currentPath = event.target.dataset.productUrl?.split('?')[0];
    if (connected && currentPath && connected !== currentPath) return;

    const mediaId = option.dataset.featuredMediaId;
    if (!mediaId) return;

    this.#showMediaById(mediaId);
  };

  /**
   * Handles a variant update event. Morphs when the media *set* changes (or desktop grid
   * needs featured-first order). Otherwise selects the featured slide in place so
   * slideshow `in-viewport` / swipe is not dropped.
   *
   * Same ordered ids still need a slide sync when an earlier skip-morph `select()` left
   * the carousel on another index and a later response sorts back to that DOM order.
   * Featured media is index 0 in that order. A buyer swipe is left where it is.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const anchor = this.closest('.shopify-section, dialog');
    if (anchor) {
      const state = getVariantApplyState(anchor);
      const scopeId = event.detail.data.productId ?? '';
      if (scopeId !== state.scopeId) {
        state.scopeId = scopeId;
        state.lastGen = 0;
      }

      const gen = event.detail.data.applyGeneration;
      if (typeof gen === 'number' && gen < state.lastGen) return;
      if (typeof gen === 'number') state.lastGen = gen;
    }

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (event.detail.data.newProduct) {
      this.#morphGalleryFromSource(newMediaGallery);
      return;
    }

    const liveIds = collectGalleryMediaIds(this);
    const newIds = collectGalleryMediaIds(newMediaGallery);

    if (!liveIds.length || !newIds.length) {
      this.#morphGalleryFromSource(newMediaGallery);
      return;
    }

    if (mediaIdsEqualOrdered(liveIds, newIds)) {
      if (this.#variantLeftFeaturedSlide) {
        const slideshow = this.slideshow;

        if (slideshow && slideshow.current !== 0) {
          void slideshow.select(0, undefined, { animate: false });
          this.refs.zoomDialogComponent?.alignToGalleryIndex(0);
        }

        this.#variantLeftFeaturedSlide = false;
      }

      return;
    }

    if (mediaIdsEqualSet(liveIds, newIds)) {
      const featuredId = newIds[0];
      if (!featuredId) {
        this.#morphGalleryFromSource(newMediaGallery);
        return;
      }

      this.#showMediaById(featuredId);

      const gridNeedsFeaturedFirst =
        this.presentation === 'grid' &&
        window.matchMedia(GRID_DESKTOP_MEDIA).matches &&
        liveIds[0] !== featuredId;

      if (gridNeedsFeaturedFirst) {
        this.#morphGalleryFromSource(newMediaGallery);
      }

      return;
    }

    this.#morphGalleryFromSource(newMediaGallery);
  };

  /**
   * In-place morph avoids `replaceWith()`, which removes the host from the DOM and can flash empty
   * layout for a frame in Safari (Chrome often composites the swap more smoothly).
   * @param {Element} newMediaGallery
   */
  #morphGalleryFromSource(newMediaGallery) {
    this.#variantLeftFeaturedSlide = false;

    morph(this, newMediaGallery, {
      ...MORPH_OPTIONS,
      childrenOnly: false,
    });

    queueMicrotask(() => {
      this.updatedCallback();
      this.#restoreSlideshowScrollAfterMorph();
      // Variant HTML lists selected variant featured media first (`sorted_media`), but
      // `initial-slide` stays `0`, so Slideshow does not re-run its initial selection. Only sync
      // when the buyer had moved away from slide 0 — avoids extra scroll/layout work on every
      // variant change (cached path, slide 0) and skips an extra rAF vs the pre-fix microtask.
      this.#selectFirstSlideAfterVariantMorphIfNeeded();
    });
  }

  /**
   * In-place morph copies server attributes onto the live `slideshow-component` and drops
   * `in-viewport`. CSS then sets `overflow: hidden` on the scroller, so native swipe dies
   * while thumbnail `select()` still works. IntersectionObserver does not re-fire while the
   * host stays on screen — re-apply the flag when the gallery is still visible.
   */
  #restoreSlideshowScrollAfterMorph() {
    const slideshow = this.slideshow;
    if (!(slideshow instanceof HTMLElement)) return;

    slideshow.updatedCallback();

    const rect = slideshow.getBoundingClientRect();
    const margin = 100;
    const onScreen =
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > -margin &&
      rect.top < (window.innerHeight || 0) + margin;

    if (onScreen) {
      slideshow.setAttribute('in-viewport', '');
    }

    if (typeof slideshow.syncViewportAfterMorph === 'function') {
      slideshow.syncViewportAfterMorph();
    }
  }

  /**
   * After variant morph, move the slideshow to slide 0 only if it was not already there.
   */
  #selectFirstSlideAfterVariantMorphIfNeeded() {
    const slideshow = this.slideshow;
    if (!slideshow?.refs?.slides?.length) return;
    if (slideshow.refs.slides.length < 2) return;
    if (slideshow.current === 0) return;

    void slideshow.select(0, undefined, { animate: false });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.#variantLeftFeaturedSlide = false;
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Buyer swipe, drag, and thumbnail clicks bubble `slideshow:select` with
   * `userInitiated`. Variant `select()` calls do not, so the round-trip pin stays.
   * @param {Event} event
   */
  #onSlideshowSelect = (event) => {
    if (!(event instanceof SlideshowSelectEvent)) return;
    if (event.target !== this.slideshow) return;
    if (!event.detail.userInitiated) return;

    this.#variantLeftFeaturedSlide = false;
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
